import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["calendarControl", "hourSelector", "programContent", "editorWrapper"];
  static values = { currentDate: String, baseUrl: String };

  // --- GETTERS ---

  // Centralized URL builder to keep things DRY
  get requestUrl() {
    if (!this.baseUrlValue) return null;

    const url = new URL(this.baseUrlValue, window.location.origin);

    // Always include the current state if available
    if (this.currentDateValue) {
      url.searchParams.append("program[day]", this.currentDateValue);
      // Also append as 'date' for your month navigation logic if the backend expects it
      url.searchParams.append("date", this.currentDateValue);
    }

    // Resilient check for hour target.
    // If the target search fails, fallback to a standard ID selector
    if (!url.searchParams.has("program[hour]")) {
      const hourEl = document.getElementById('hour');
      url.searchParams.set("program[hour]", hourEl ? hourEl.value : "0");
    }

    return url.toString();
  }

  // --- ACTIONS ---

  async loadProgramTemplate(event) {
    event.preventDefault();
    this.baseUrlValue = event.currentTarget.dataset.programUrl;

    if (this.hasEditorWrapperTarget) this.editorWrapperTarget.classList.remove('hidden');
    window.scrollTo({ top: 0, behavior: 'smooth' });

    await this.fetchProgramData(this.requestUrl);
  }

  async previousMonth(event) {
    event.preventDefault();
    this.navigateMonth(-1);
  }

  async nextMonth(event) {
    event.preventDefault();
    this.navigateMonth(1);
  }

  async selectDay(event) {
    event.stopPropagation();
    event.preventDefault();

    // 1. Update the state
    this.currentDateValue = event.currentTarget.dataset.newDate;

    // 2. Fetch using the centralized URL builder
    await this.fetchProgramData(this.requestUrl);
  }

  // New action: call this from data-action="change->calendar#updateHour"
  async updateHour() {
    await this.fetchProgramData(this.requestUrl);
  }

  // --- HELPERS ---

  async navigateMonth(direction) {
    let date = this.currentDateValue ? new Date(this.currentDateValue + "T00:00:00") : new Date();
    date.setMonth(date.getMonth() + direction);

    this.currentDateValue = date.toISOString().split('T')[0];
    await this.fetchProgramData(this.requestUrl);
  }

  async fetchProgramData(url) {
    if (!url) return;
    try {
      const response = await fetch(url, { headers: { "Accept": "application/json" } });
      if (!response.ok) throw new Error("Network response was not ok");

      const data = await response.json();
      this.updateUI(data);
    } catch (error) {
      console.error("Failed to fetch program data:", error);
    }
  }

  updateUI(data) {
    const mappings = [
      { target: "calendarControl", key: "calendar_control" },
      { target: "programContent", key: "program_content" },
      { target: "hourSelector", key: "hour_selector" }
    ];

    mappings.forEach(({ target, key }) => {
      const targetName = `${target}Target`;
      if (this[`has${target.charAt(0).toUpperCase() + target.slice(1)}Target`] && data[key]) {
        this[targetName].innerHTML = data[key];
      }
    });

    if (data.selected_date) this.currentDateValue = data.selected_date;
  }
}
